import { ChangeEvent, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { Registration } from '@/types/registration.types';
import useComputedRegistrationSettings from '../../hooks/use-computed-registration-settings';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { validateUrl } from '@/utils/validate-url';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import Container from '@ui/container/container';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';
import Text from '@ui/text/text/text';
import Link from '@ui/links/link';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import IconWithBackdrop from '@ui/icons/icon-with-backdrop';
import { ICONS } from '@ui/icons/types';

const ExternalRegistrationLink = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const dispatch = useAppDispatch();
  const { registrationSettings } = useComputedRegistrationSettings();
  const savingWorkingProject = useAppSelector(selectIsSavingWorkingProject);

  const disableNativeRegistrationIsOn = registrationSettings?.externalRegistration?.isOn;
  const [externalUrl, setExternalUrl] = useState<string | undefined | null>(
    registrationSettings?.externalRegistration?.url
  );
  const [error, setError] = useState('');

  const setExernalUrl = (value: string | null) => {
    if (!registrationSettings) return;

    const updatedUrl = value?.trim() || null;

    setError('');
    setExternalUrl(updatedUrl);
    if (inputRef.current && !updatedUrl) {
      inputRef.current.value = '';
    }
    if (updatedUrl && !validateUrl(updatedUrl)) {
      setError('Invalid URL');
      return;
    }
    const currentUrl = registrationSettings?.externalRegistration?.url;
    if (updatedUrl === currentUrl) {
      return;
    }

    const updatedSettings: Registration = {
      ...registrationSettings,
      externalRegistration: {
        ...registrationSettings?.externalRegistration,
        url: updatedUrl,
      },
    };

    dispatch(saveWorkingProjectByFields({ registration_settings: updatedSettings }));
  };

  return (
    <SlideDownCard open={!!disableNativeRegistrationIsOn} autoGrow animateOnInitialLoad={false}>
      <div className="flex-column jc-flex-start gap-8">
        <div className="flex ai-center gap-8">
          <Text tag="h5" className="H5" design="GlobalIcoText1">
            Registration Link
          </Text>
          <TooltipPortal tooltipContent={<Text design="GlobalIcoText1">Registration Link</Text>}>
            <IconWithBackdrop
              name={ICONS.INFO_CIRCLE}
              size={12}
              color="var(--primitives-mode-1-gray-500)"
              backdropColor="var(--primitives-mode-1-gray-100)"
            />
          </TooltipPortal>
        </div>
        <Container design="GlobalCont1" className="flex ai-flex-start jc-space-evenly padding-16 radius-12">
          <div className="flex-column gap-4 flex-1">
            <Text tag="h6" className="H6" design="GlobalIcoText1">
              URL
            </Text>
            <Text className="C2" design="GlobalIcoText3">
              Manage 3rd Party registrations in the{' '}
              <Link design="standard_link" to="../integrations" relative="path">
                Integrations tab
              </Link>
            </Text>
          </div>
          <div className="flex-1 margin-t-2">
            <div>
              <InputWithOverlay
                typography="P2"
                inputRef={inputRef}
                inputProps={{
                  type: 'url',
                  defaultValue: externalUrl || '',
                  placeholder: 'Add a registration URL',
                  disabled: !disableNativeRegistrationIsOn || savingWorkingProject,
                  onBlur: (e: ChangeEvent<HTMLInputElement>) => setExernalUrl(e.target.value),
                }}
                inputHeight="36px"
                dirtyError={({ dirty, setHasError }) => {
                  setHasError(dirty && !!(externalUrl && !validateUrl(externalUrl)));
                }}
              />
              <SlideDownCard open={!!error} autoGrow>
                <Text color="var(--global-error-500)" className="P1 margin-l-12">
                  {error}
                </Text>
              </SlideDownCard>
            </div>
          </div>
        </Container>
      </div>
    </SlideDownCard>
  );
};

export default ExternalRegistrationLink;
